import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ROIKpiCard from '../components/dashboard/ROIKpiCard';
import SetupBanner from '../components/SetupBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Calendar as CalendarIcon,
  ArrowRight,
  ExternalLink,
  Filter
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_TICK = { fontSize: 12, fill: '#737373' };
const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#0A0A0A',
  border: 'none',
  borderRadius: '4px',
  color: '#fff',
  fontFamily: 'JetBrains Mono',
};
const CHART_DOT = { fill: '#002FA7', strokeWidth: 0, r: 4 };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [consultantStats, setConsultantStats] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedConsultant !== 'all') {
      fetchFilteredData();
    }
  }, [selectedConsultant]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, meetingsRes, consultantsRes, consultantStatsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/meetings`, { withCredentials: true }),
        axios.get(`${API}/consultants`, { withCredentials: true }),
        axios.get(`${API}/consultants/stats`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRecentClients(clientsRes.data.slice(0, 5));
      setConsultants(consultantsRes.data);
      setConsultantStats(consultantStatsRes.data);
      
      const now = new Date();
      const upcoming = meetingsRes.data
        .filter(m => new Date(m.date) >= now && m.status === 'scheduled')
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
      setUpcomingMeetings(upcoming);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredData = async () => {
    try {
      const clientsRes = await axios.get(`${API}/clients`, { withCredentials: true });
      const filteredClients = clientsRes.data.filter(c => c.consultant_id === selectedConsultant);
      setRecentClients(filteredClients.slice(0, 5));
      
      // Update stats for selected consultant
      const consultantStat = consultantStats.find(s => s.consultant_id === selectedConsultant);
      if (consultantStat) {
        setStats(prev => ({
          ...prev,
          total_clients: consultantStat.clients_count,
          total_keywords: consultantStat.keywords_count,
          keywords_improved: consultantStat.keywords_improved,
          keywords_declined: consultantStat.keywords_declined
        }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleConsultantChange = (value) => {
    setSelectedConsultant(value);
    if (value === 'all') {
      fetchData();
    }
  };

  const mockRankingData = [
    { date: 'Jan', position: 45 },
    { date: 'Fév', position: 38 },
    { date: 'Mar', position: 32 },
    { date: 'Avr', position: 28 },
    { date: 'Mai', position: 22 },
    { date: 'Jun', position: 18 },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6" data-testid="dashboard-loading">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-neutral-200 rounded" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <SetupBanner />
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Tableau de bord
            </h1>
            <p className="text-neutral-500 mt-1">Vue d'ensemble de votre activité SEO</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-neutral-500" />
              <Select value={selectedConsultant} onValueChange={handleConsultantChange}>
                <SelectTrigger className="w-[200px]" data-testid="consultant-filter">
                  <SelectValue placeholder="Tous les consultants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les consultants</SelectItem>
                  {consultants.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Link 
              to="/reports" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded hover:bg-[#002277] transition-colors"
              data-testid="generate-report-btn"
            >
              Générer un rapport
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-neutral-200" data-testid="kpi-grid">
          {/* Total Clients */}
          <div className="kpi-card grid-border-r grid-border-b">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  Clients actifs
                </p>
                <p className="font-mono text-4xl font-bold text-neutral-900 mt-2">
                  {stats?.total_clients || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-[#002FA7]/10 rounded flex items-center justify-center">
                <Users className="w-5 h-5 text-[#002FA7]" />
              </div>
            </div>
          </div>

          {/* Total Keywords */}
          <div className="kpi-card grid-border-r grid-border-b">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  Mots-clés suivis
                </p>
                <p className="font-mono text-4xl font-bold text-neutral-900 mt-2">
                  {stats?.total_keywords || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-[#002FA7]/10 rounded flex items-center justify-center">
                <Target className="w-5 h-5 text-[#002FA7]" />
              </div>
            </div>
          </div>

          {/* Keywords Improved */}
          <div className="kpi-card grid-border-r grid-border-b">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  Positions améliorées
                </p>
                <p className="font-mono text-4xl font-bold text-green-600 mt-2">
                  +{stats?.keywords_improved || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-50 rounded flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>

          {/* Keywords Declined */}
          <div className="kpi-card grid-border-b">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  Positions en baisse
                </p>
                <p className="font-mono text-4xl font-bold text-red-500 mt-2">
                  -{stats?.keywords_declined || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-50 rounded flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* ROI KPI (admin-visible data) */}
        <ROIKpiCard />

        {/* Charts and Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ranking Trend Chart */}
          <div className="lg:col-span-2 bg-white border border-neutral-200 p-6" data-testid="ranking-chart">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                Évolution moyenne des positions
              </h2>
              <span className="text-xs font-mono text-neutral-500">6 DERNIERS MOIS</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mockRankingData}>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={CHART_TICK}
                  />
                  <YAxis 
                    reversed 
                    domain={[1, 50]}
                    axisLine={false}
                    tickLine={false}
                    tick={CHART_TICK}
                  />
                  <Tooltip 
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(value) => [`Position ${value}`, '']}
                  />
                  <Line 
                    type="linear" 
                    dataKey="position" 
                    stroke="#002FA7" 
                    strokeWidth={2}
                    dot={CHART_DOT}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Lead Sources */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="lead-sources">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                Sources de leads
              </h2>
              <span className="font-mono text-2xl font-bold text-neutral-900">
                {stats?.leads_this_month || 0}
              </span>
            </div>
            <div className="space-y-4">
              {stats?.lead_sources?.map((source, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-neutral-700">{source.source}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-neutral-900">{source.count}</span>
                    <span className={`font-mono text-xs ${source.trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {source.trend >= 0 ? '+' : ''}{source.trend}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Clients and Upcoming Meetings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Clients */}
          <div className="bg-white border border-neutral-200" data-testid="recent-clients">
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                Clients récents
              </h2>
              <Link 
                to="/clients" 
                className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
                data-testid="view-all-clients-link"
              >
                Voir tous
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-neutral-100">
              {recentClients.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                  Aucun client pour le moment
                </div>
              ) : (
                recentClients.map((client) => (
                  <Link 
                    key={client.id} 
                    to={`/clients/${client.id}`}
                    className="flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-neutral-900">{client.name}</p>
                      <p className="text-sm text-neutral-500 flex items-center gap-1">
                        {client.website}
                        <ExternalLink className="w-3 h-3" />
                      </p>
                    </div>
                    <span className={`text-xs font-mono px-2 py-1 rounded ${
                      client.status === 'active' 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {client.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Upcoming Meetings */}
          <div className="bg-white border border-neutral-200" data-testid="upcoming-meetings">
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                Prochains entretiens
              </h2>
              <Link 
                to="/calendar" 
                className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
                data-testid="view-calendar-link"
              >
                Calendrier
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-neutral-100">
              {upcomingMeetings.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                  Aucun entretien planifié
                </div>
              ) : (
                upcomingMeetings.map((meeting) => (
                  <div key={meeting.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-neutral-900">{meeting.title}</p>
                        <p className="text-sm text-neutral-500">{meeting.client_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm text-neutral-900">
                          {new Date(meeting.date).toLocaleDateString('fr-FR', { 
                            day: '2-digit',
                            month: 'short'
                          })}
                        </p>
                        <p className="font-mono text-xs text-neutral-500">
                          {new Date(meeting.date).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
